/**
 * webQueryPersister — armazenamento IndexedDB para o cache do React Query na WEB.
 *
 * POR QUÊ NÃO localStorage: o `AsyncStorage` do react-native-web É o
 * `localStorage` — SÍNCRONO na main thread. O cache persistido antigo reunia até
 * 30 dias de queries num único JSON: `JSON.parse` no restore bloqueava o início
 * das consultas e cada atualização reserializava o blob inteiro em celulares
 * modestos. Por isso a persistência web foi removida (ver `QueryProvider`).
 *
 * POR QUÊ VOLTAR: sem persistência nenhuma, **toda recarga de aba é partida
 * fria** — o aluno recarrega e o Perfil volta a carregar do zero. O IndexedDB
 * resolve os dois lados: é assíncrono (não bloqueia a main thread) e guarda o
 * valor sem serializar tudo a cada escrita da mesma forma.
 *
 * ESCOPO: guardamos POUCO de propósito — só o que a `QueryProvider` autoriza na
 * allowlist. Isto não é armazenamento offline (isso é o nativo); é uma ponte
 * para a recarga de aba.
 *
 * Toda operação degrada para no-op silencioso: IndexedDB **lança ou fica
 * indisponível** em modo privado, em contexto de terceiro e com site data
 * bloqueado. Falhar aqui jamais pode impedir o app de funcionar — sem storage,
 * o comportamento volta a ser exatamente o de hoje (cache só em memória).
 */

const DB_NAME = 'uniquest-rq';
const STORE = 'cache';
const DB_VERSION = 1;

let dbPromise = null;

const openDb = () => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve) => {
        try {
            if (typeof indexedDB === 'undefined') return resolve(null);
            const req = indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => resolve(null);
            // Outra aba pediu upgrade: soltar a conexão em vez de travar as duas.
            req.onblocked = () => resolve(null);
        } catch {
            resolve(null);
        }
    });
    return dbPromise;
};

const withStore = async (mode, fn) => {
    const db = await openDb();
    if (!db) return null;
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(STORE, mode);
            const store = tx.objectStore(STORE);
            const req = fn(store);
            tx.oncomplete = () => resolve(req ? req.result : null);
            tx.onerror = () => resolve(null);
            tx.onabort = () => resolve(null);
        } catch {
            resolve(null);
        }
    });
};

// Teto de leitura. CRÍTICO: o `PersistQueryClientProvider` mantém as queries
// PAUSADAS enquanto restaura. Se o IndexedDB pendurar (aba concorrente segurando
// um upgrade, storage sob pressão, bug de navegador), o restore nunca terminaria
// e o app ficaria carregando para sempre — trocaríamos "recarga fria" por "app
// travado", um negócio péssimo. Passado o teto, seguimos sem cache: o pior caso
// vira exatamente o comportamento atual.
const READ_TIMEOUT_MS = 1500;

const withTimeout = (promise, ms) => Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(null), ms)),
]);

/**
 * Interface mínima que o `createAsyncStoragePersister` espera
 * (`getItem`/`setItem`/`removeItem` assíncronos).
 */
export const webIdbStorage = {
    getItem: (key) => withTimeout(withStore('readonly', (store) => store.get(key)), READ_TIMEOUT_MS),
    // Escrita/remoção não bloqueiam nada visível ao usuário — sem teto, mas
    // igualmente à prova de falha (withStore resolve null em qualquer erro).
    setItem: (key, value) => withStore('readwrite', (store) => store.put(value, key)),
    removeItem: (key) => withStore('readwrite', (store) => store.delete(key)),
};

export default webIdbStorage;
